import { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import http from '../../../../shared/http'
import { TApi } from '../../../../shared/const'
import { askConfirm } from '../../../../shared/confirmDialog'
import { formatClock, measureVideoDuration, mediaUrl, uploadInChunks } from '../../../../shared/courses'
import { loadMediaSettings, uploadToBunny } from '../../../../shared/bunnyUpload'

import Btn from '../../../../UI/Btn'
import { RichTextEditor } from '../../../../UI/RichText'

import styles from './style.module.scss'

const MAX_VIDEO_MB = 500

/** Eine Liste um eine Stelle verschieben; liefert die neue Reihenfolge der Kennungen. */
export const moveId = (ids, index, richtung) => {
	const ziel = index + richtung
	if (ziel < 0 || ziel >= ids.length) return ids
	const neu = [...ids]
	;[neu[index], neu[ziel]] = [neu[ziel], neu[index]]
	return neu
}

/**
 * Module und Lektionen eines Kurses.
 *
 * Reihenfolge per Pfeil statt per Ziehen: Ziehen ist auf langen Listen mit
 * Videos darin fummelig, und eine verrutschte Lektion fällt erst auf, wenn
 * Schüler:innen sie in der falschen Reihenfolge sehen.
 */
export default function ContentTab({ course, onChanged, markDirty }) {
	const { t } = useTranslation()
	const modules = course.modules || []

	const modulAnlegen = async () => {
		const titel = await askConfirm({
			title: t('New module'),
			confirmLabel: t('Create'),
			prompt: { label: t('Title'), error: t('Please enter a title.') }
		})
		if (typeof titel !== 'string' || !titel.trim()) return
		http.post(TApi.ADMIN_COURSE_MODULES(course.id), { title: titel.trim() }).then(() => onChanged())
	}

	const modulVerschieben = (index, richtung) => {
		const ids = moveId(
			modules.map(modul => modul.id),
			index,
			richtung
		)
		http.put(TApi.ADMIN_COURSE_MODULES_ORDER(course.id), { ids }).then(() => onChanged())
	}

	return (
		<div>
			{modules.length === 0 && <p className={styles.intro}>{t('This course has no modules yet.')}</p>}

			{modules.map((modul, index) => (
				<ModuleCard
					key={modul.id}
					modul={modul}
					index={index}
					count={modules.length}
					onMove={richtung => modulVerschieben(index, richtung)}
					onChanged={onChanged}
					markDirty={markDirty}
				/>
			))}

			<Btn onClick={modulAnlegen}>{t('Add module')}</Btn>
		</div>
	)
}

ContentTab.propTypes = {
	course: PropTypes.object.isRequired,
	onChanged: PropTypes.func.isRequired,
	markDirty: PropTypes.func.isRequired
}

function ModuleCard({ modul, index, count, onMove, onChanged, markDirty }) {
	const { t } = useTranslation()
	const [titel, setTitel] = useState(modul.title || '')
	const [text, setText] = useState(modul.description || '')
	const [offenId, setOffenId] = useState(null)

	useEffect(() => {
		setTitel(modul.title || '')
		setText(modul.description || '')
	}, [modul.title, modul.description])

	const geaendert = titel !== (modul.title || '') || text !== (modul.description || '')
	useEffect(() => markDirty(`module-${modul.id}`, geaendert), [geaendert, markDirty, modul.id])
	useEffect(() => () => markDirty(`module-${modul.id}`, false), [markDirty, modul.id])

	const speichern = () => {
		if (!titel.trim()) return toast.error(t('Please enter a title.'))
		http.put(TApi.ADMIN_COURSE_MODULE(modul.id), { title: titel.trim(), description: text }).then(() => {
			toast.success(t('Saved'))
			onChanged()
		})
		return undefined
	}

	const loeschen = async () => {
		const ok = await askConfirm({
			title: t('Delete module?'),
			text: t('The module is deleted with all its lessons, videos and questions.'),
			confirmLabel: t('Delete'),
			tone: 'danger'
		})
		if (ok) http.delete(TApi.ADMIN_COURSE_MODULE(modul.id)).then(() => onChanged())
	}

	const lektionAnlegen = kind => {
		http.post(TApi.ADMIN_COURSE_LESSONS(modul.id), {
			kind,
			title: kind === 'TEXT' ? t('New reading') : t('New video')
		}).then(({ data }) => {
			setOffenId(data?.id || null)
			onChanged()
		})
	}

	const lektionVerschieben = (lessonIndex, richtung) => {
		const ids = moveId(
			(modul.lessons || []).map(lesson => lesson.id),
			lessonIndex,
			richtung
		)
		http.put(TApi.ADMIN_COURSE_LESSONS_ORDER(modul.id), { ids }).then(() => onChanged())
	}

	const lessons = modul.lessons || []

	return (
		<section className={styles.module}>
			<div className={styles.moduleHead}>
				<span className={styles.moduleNo}>{t('Module {{n}}', { n: index + 1 })}</span>
				<input
					className={styles.moduleTitle}
					value={titel}
					aria-label={t('Module title')}
					onChange={e => setTitel(e.target.value)}
				/>
				<button
					type="button"
					className={styles.iconBtn}
					disabled={index === 0}
					onClick={() => onMove(-1)}
					aria-label={t('Move up')}
				>
					↑
				</button>
				<button
					type="button"
					className={styles.iconBtn}
					disabled={index === count - 1}
					onClick={() => onMove(1)}
					aria-label={t('Move down')}
				>
					↓
				</button>
				<button type="button" className={styles.iconBtn} onClick={loeschen} aria-label={t('Delete module')}>
					✕
				</button>
			</div>

			<div className={styles.moduleBody}>
				<label className={styles.field}>
					{t('Description')}
					<textarea value={text} onChange={e => setText(e.target.value)} />
				</label>
				{geaendert && (
					<div className={styles.buttons} style={{ marginBottom: 14 }}>
						<Btn attention onClick={speichern}>
							{t('Save module')}
						</Btn>
					</div>
				)}

				<h4 className={styles.subTitle}>
					{t('Lessons')} ({lessons.length})
				</h4>

				{lessons.map((lesson, lessonIndex) => (
					<LessonEditor
						key={lesson.id}
						lesson={lesson}
						index={lessonIndex}
						count={lessons.length}
						open={offenId === lesson.id}
						onToggle={() => setOffenId(offenId === lesson.id ? null : lesson.id)}
						onMove={richtung => lektionVerschieben(lessonIndex, richtung)}
						onChanged={onChanged}
						markDirty={markDirty}
					/>
				))}

				<div className={styles.buttons}>
					<Btn theme="secondary" onClick={() => lektionAnlegen('VIDEO')}>
						{t('Add video lesson')}
					</Btn>
					<Btn theme="secondary" onClick={() => lektionAnlegen('TEXT')}>
						{t('Add reading')}
					</Btn>
				</div>
			</div>
		</section>
	)
}

ModuleCard.propTypes = {
	modul: PropTypes.object.isRequired,
	index: PropTypes.number.isRequired,
	count: PropTypes.number.isRequired,
	onMove: PropTypes.func.isRequired,
	onChanged: PropTypes.func.isRequired,
	markDirty: PropTypes.func.isRequired
}

const lessonFields = lesson => ({
	kind: lesson.kind || 'VIDEO',
	title: lesson.title || '',
	description: lesson.description || '',
	body: lesson.body || '',
	transcript: lesson.transcript || ''
})

function LessonEditor({ lesson, index, count, open, onToggle, onMove, onChanged, markDirty }) {
	const { t } = useTranslation()
	const ausgang = useMemo(() => lessonFields(lesson), [lesson])
	const [werte, setWerte] = useState(ausgang)
	const [upload, setUpload] = useState(null)

	useEffect(() => setWerte(ausgang), [ausgang])

	const geaendert = JSON.stringify(werte) !== JSON.stringify(ausgang)
	useEffect(() => markDirty(`lesson-${lesson.id}`, geaendert), [geaendert, markDirty, lesson.id])
	useEffect(() => () => markDirty(`lesson-${lesson.id}`, false), [markDirty, lesson.id])

	const setze = (feld, wert) => setWerte(vorher => ({ ...vorher, [feld]: wert }))

	const speichern = () => {
		if (!werte.title.trim()) return toast.error(t('Please enter a title.'))
		http.put(TApi.ADMIN_COURSE_LESSON(lesson.id), { ...werte, title: werte.title.trim() }).then(() => {
			toast.success(t('Saved'))
			onChanged()
		})
		return undefined
	}

	const loeschen = async () => {
		const ok = await askConfirm({
			title: t('Delete lesson?'),
			text: t('The lesson and its video are deleted. Progress already recorded for it is lost.'),
			confirmLabel: t('Delete'),
			tone: 'danger'
		})
		if (ok) http.delete(TApi.ADMIN_COURSE_LESSON(lesson.id)).then(() => onChanged())
	}

	const videoHochladen = async event => {
		const datei = event.target.files?.[0]
		event.target.value = ''
		if (!datei) return
		if (datei.type && datei.type !== 'video/mp4') return toast.error(t('Please choose an MP4 video.'))
		if (datei.size > MAX_VIDEO_MB * 1024 * 1024) {
			return toast.error(t('The video may be at most {{n}} MB.', { n: MAX_VIDEO_MB }))
		}

		const dauer = await measureVideoDuration(datei)
		const { bunny } = await loadMediaSettings()

		setUpload(0)
		// Mit Bunny direkt dorthin (tus-js-client erst hier geladen), sonst in Stücken über den Server.
		const hochladen = bunny
			? import('tus-js-client').then(({ Upload }) =>
					uploadToBunny({
						file: datei,
						duration: dauer,
						Upload,
						onProgress: setUpload,
						start: () =>
							http.post(TApi.ADMIN_COURSE_LESSON_VIDEO_BUNNY(lesson.id), {}, { disableToast: true }).then(({ data }) => data),
						finish: body =>
							http
								.post(TApi.ADMIN_COURSE_LESSON_VIDEO_BUNNY_COMPLETE(lesson.id), body, { disableToast: true })
								.then(({ data }) => data)
					})
			  )
			: uploadInChunks({
					file: datei,
					duration: dauer,
					onProgress: setUpload,
					send: (form, stueckFortschritt) =>
						http
							.post(TApi.ADMIN_COURSE_LESSON_VIDEO_CHUNK(lesson.id), form, {
								// Fehler eines Stücks meldet der Vorgang am Ende einmal — nicht je Anlauf.
								disableToast: true,
								onUploadProgress: ereignis => {
									if (ereignis.total) stueckFortschritt(ereignis.loaded / ereignis.total)
								}
							})
							.then(({ data }) => data)
			  })

		hochladen
			.then(() => {
				toast.success(t(bunny ? 'Video uploaded — Bunny is preparing it, this takes a few minutes.' : 'Video uploaded'))
				onChanged()
			})
			.catch(() => toast.error(t('The video could not be uploaded. Please try again.')))
			.finally(() => setUpload(null))
		return undefined
	}

	const bildHochladen = event => {
		const datei = event.target.files?.[0]
		event.target.value = ''
		if (!datei) return
		const form = new FormData()
		form.append('file', datei)
		http.post(TApi.ADMIN_COURSE_LESSON_THUMBNAIL(lesson.id), form).then(() => onChanged())
	}

	const hatVideo = lesson.hasVideo || Boolean(lesson.videoName) || Boolean(lesson.videoGuid)

	return (
		<div className={styles.lesson}>
			<div
				className={styles.lessonHead}
				role="button"
				tabIndex={0}
				onClick={onToggle}
				onKeyDown={event => {
					if (event.key === 'Enter' || event.key === ' ') {
						event.preventDefault()
						onToggle()
					}
				}}
			>
				<span
					className={styles.lessonThumb}
					style={lesson.thumbUrl ? { backgroundImage: `url("${mediaUrl(lesson.thumbUrl)}")` } : undefined}
				/>
				<span>
					<span className={styles.lessonName}>
						{index + 1}. {lesson.title}
					</span>
					<br />
					<span className={styles.lessonMeta}>
						{lesson.kind === 'TEXT'
							? t('Reading')
							: hatVideo
							? `${t('Video')} · ${formatClock(lesson.durationSec)}`
							: t('Video missing')}
						{lesson.transcript ? ` · ${t('Transcript')}` : ''}
						{geaendert ? ` · ${t('unsaved')}` : ''}
					</span>
				</span>
				<span className={styles.lessonTools} data-row-ignore>
					<button
						type="button"
						className={styles.iconBtn}
						disabled={index === 0}
						onClick={event => {
							event.stopPropagation()
							onMove(-1)
						}}
						aria-label={t('Move up')}
					>
						↑
					</button>
					<button
						type="button"
						className={styles.iconBtn}
						disabled={index === count - 1}
						onClick={event => {
							event.stopPropagation()
							onMove(1)
						}}
						aria-label={t('Move down')}
					>
						↓
					</button>
					<button
						type="button"
						className={styles.iconBtn}
						onClick={event => {
							event.stopPropagation()
							loeschen()
						}}
						aria-label={t('Delete lesson')}
					>
						✕
					</button>
				</span>
			</div>

			{open && (
				<div className={styles.lessonBody}>
					<div className={styles.row}>
						<label className={styles.field}>
							{t('Title')}
							<input type="text" value={werte.title} onChange={e => setze('title', e.target.value)} />
						</label>
						<label className={styles.field} style={{ flex: '0 0 180px' }}>
							{t('Type')}
							<select value={werte.kind} onChange={e => setze('kind', e.target.value)}>
								<option value="VIDEO">{t('Video')}</option>
								<option value="TEXT">{t('Reading')}</option>
							</select>
						</label>
					</div>
					<label className={styles.field}>
						{t('Short description')}
						<textarea value={werte.description} onChange={e => setze('description', e.target.value)} />
					</label>

					{werte.kind === 'VIDEO' && (
						<>
							<div className={styles.upload}>
								<b>{t('Video')}:</b>
								<span>
									{hatVideo
										? `${lesson.videoName || lesson.videoGuid || t('uploaded')} · ${formatClock(lesson.durationSec)}`
										: t('none yet')}
								</span>
								<label>
									<input type="file" accept="video/mp4" onChange={videoHochladen} disabled={upload !== null} />
								</label>
								{upload !== null && (
									<>
										<span className={styles.progress}>
											<span style={{ width: `${upload}%` }} />
										</span>
										<span>{upload} %</span>
									</>
								)}
							</div>
							<div className={styles.upload}>
								<b>{t('Thumbnail')}:</b>
								<label>
									<input type="file" accept="image/jpeg,image/png,image/webp" onChange={bildHochladen} />
								</label>
								<span className={styles.fieldHint}>{t('16:9, e.g. 640 × 360.')}</span>
							</div>
							<label className={styles.field}>
								{t('Transcript')}
								<textarea
									value={werte.transcript}
									rows={8}
									onChange={e => setze('transcript', e.target.value)}
								/>
								<span className={styles.fieldHint}>
									{t('Shown below the video; also helps with accessibility.')}
								</span>
							</label>
						</>
					)}

					{werte.kind === 'TEXT' && (
						<div className={styles.field}>
							{t('Text')}
							<RichTextEditor
								className={styles.richText}
								value={werte.body}
								ariaLabel={t('Text')}
								onChange={wert => setze('body', wert)}
							/>
						</div>
					)}

					<div className={styles.buttons}>
						<Btn attention={geaendert} disabled={!geaendert} onClick={speichern}>
							{t('Save lesson')}
						</Btn>
						{geaendert && (
							<Btn theme="minimal" onClick={() => setWerte(ausgang)}>
								{t('Undo changes')}
							</Btn>
						)}
					</div>
				</div>
			)}
		</div>
	)
}

LessonEditor.propTypes = {
	lesson: PropTypes.object.isRequired,
	index: PropTypes.number.isRequired,
	count: PropTypes.number.isRequired,
	open: PropTypes.bool.isRequired,
	onToggle: PropTypes.func.isRequired,
	onMove: PropTypes.func.isRequired,
	onChanged: PropTypes.func.isRequired,
	markDirty: PropTypes.func.isRequired
}
